import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DrugCardType,
  IAdminTokenPayload,
  ICustomerUser,
  IDrugCard,
  IFile,
  VerifiedStatus
} from '@care-portals/api-interfaces';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Subject, takeUntil } from 'rxjs';
import { AuthFacade } from '../../../+store/auth/auth.facade';
import { CustomerFacade } from '../../../+store/customer/customer.facade';
import { FileFacade } from '../../../+store/file/file.facade';

const DRUG_CARDS_FOLDER = 'drug-cards';

const DRUG_CARD_TYPE_LABELS: Record<string, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  secondary2: 'Secondary 2',
  secondary3: 'Secondary 3',
  provincial: 'Provincial'
};

/**
 * Reusable modal for a customer's documents. Used from both the order drawer
 * (customer-summary) and the customer profile page (customer-details).
 *
 * Call `open(filter)` via a template ref / ViewChild:
 *   - 'govId'   → ID + selfie documents with the verification footer
 *   - 'drugCard'→ read-only drug-card detail (type, relationship, front/back images)
 *   - undefined → all documents
 */
@Component({
  selector: 'portals-emr-customer-documents-modal',
  templateUrl: './customer-documents-modal.component.html',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    FormsModule,
    NzButtonModule,
    NzCardModule,
    NzDatePickerModule,
    NzModalModule,
    NzSpinModule
  ]
})
export class CustomerDocumentsModalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() customer: ICustomerUser;
  @Output() verificationChanged = new EventEmitter<void>();

  private readonly isDestroyed$ = new Subject<void>();
  private user: IAdminTokenPayload;

  isIdImagesModalVisible = false;
  idImagesTitle = 'ID Documents';
  idFiles: IFile[] = [];
  idFilesLoading = false;
  activeIdFilter: 'drugCard' | 'govId' | null = null;
  private filesLoadedForCustomer: string | null = null;
  private idFileRequestedIds = new Set<string>();

  isExpiryModalVisible = false;
  expiryDate: Date | null = null;
  isVerificationConfirmationModalVisible = false;

  constructor(
    private readonly fileFacade: FileFacade,
    private readonly customerFacade: CustomerFacade,
    private readonly authFacade: AuthFacade
  ) {}

  private get customerId(): string {
    return this.customer?._id;
  }

  get IDVerificationStatus() {
    return this.customer?.verifications?.govId?.verified;
  }

  get IDVerifiedBy() {
    return this.customer?.verifications?.govId?.verifiedBy;
  }

  get drugCards(): IDrugCard[] {
    return Array.isArray(this.customer?.drugCards) ? this.customer.drugCards : [];
  }

  ngOnInit(): void {
    this.authFacade.user$
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(user => (this.user = user));

    this.fileFacade.recentFiles$
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(files => {
        // Legacy drug cards keep their original upload filename (not the
        // `drugCard-{uid}` convention), so the name regex alone would drop them.
        // Also admit any file whose name matches a known drug-card key.
        const cardKeys = new Set(
          this.drugCards.flatMap(
            c =>
              [c.frontFileKey, c.backFileKey, c.fileKey].filter(
                Boolean
              ) as string[]
          )
        );
        this.idFiles = files.filter(
          f =>
            f.tags?.includes('ids') ||
            /^(govId|selfie|drugCard)/i.test(f.name) ||
            cardKeys.has(f.name)
        );
        this.idFilesLoading = false;
        this.idFiles.forEach(file => {
          const fileKey = file.folder ? `${file.folder}/${file.name}` : file.name;
          if ((!file.loaded || !file.buffer) && !this.idFileRequestedIds.has(fileKey)) {
            this.idFileRequestedIds.add(fileKey);
            this.fileFacade.getFile(
              this.customerId,
              file.name,
              file.folder || undefined
            );
          }
        });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['customer'];
    if (change && change.previousValue?._id !== change.currentValue?._id) {
      this.idFileRequestedIds.clear();
      this.filesLoadedForCustomer = null;
      this.idFiles = [];
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  open(filter?: 'drugCard' | 'govId'): void {
    // Allow the recentFiles$ loop to re-fetch any file whose buffer was wiped
    // from the shared store (by another loadFiles) since the last open.
    this.idFileRequestedIds.clear();
    this.activeIdFilter = filter || null;
    this.idImagesTitle =
      filter === 'drugCard'
        ? 'Drug Card Documents'
        : filter === 'govId'
        ? 'ID & Selfie Documents'
        : 'ID Documents';
    this.isIdImagesModalVisible = true;
    this.idFilesLoading = this.filesLoadedForCustomer !== this.customerId;
    if (this.filesLoadedForCustomer !== this.customerId) {
      this.filesLoadedForCustomer = this.customerId;
      this.fileFacade.loadFiles(this.customerId, '');
    }
    // Load drug card files individually (upsertOne) so they merge with root files
    this.drugCards.forEach(card => {
      [card.frontFileKey, card.backFileKey, card.fileKey]
        .filter(Boolean)
        .forEach(key =>
          this.fileFacade.getFile(this.customerId, key, DRUG_CARDS_FOLDER)
        );
    });
  }

  // ── Flat ID / selfie list (govId or all) ──────────────────────────────

  getDisplayedIdFiles(): IFile[] {
    if (!this.activeIdFilter) return this.idFiles;
    if (this.activeIdFilter === 'drugCard') {
      return this.idFiles.filter(f => /^drugCard/i.test(f.name));
    }
    return this.idFiles.filter(f => /^(govId|selfie|persona)/i.test(f.name));
  }

  getIdFileLabel(name: string): string {
    const personaLabels: Record<string, string> = {
      'persona-id': 'Gov. photo ID (Persona)',
      'persona-selfie': 'Selfie (Persona)'
    };
    const baseName = name?.split('.')[0];
    if (personaLabels[baseName]) return personaLabels[baseName];
    if (/^drugCard-/i.test(name)) return 'Drug plan card';

    const prefix = name?.split('_')[0];
    const labels: Record<string, string> = {
      govId: 'Gov. photo ID',
      selfie: 'Selfie photo',
      drugCard1: 'Drug plan card',
      drugCard2: 'Drug plan card 2',
      drugCard3: 'Drug plan card 3'
    };
    return labels[prefix] || prefix || name;
  }

  getIdImageSrc(file: IFile): string {
    const raw = typeof file?.buffer === 'string' ? file.buffer.trim() : '';
    if (!raw) return '';
    if (
      raw.startsWith('data:') ||
      raw.startsWith('blob:') ||
      raw.startsWith('http')
    ) {
      return raw;
    }
    const cleaned = raw.replace(/\s/g, '');
    const mime = file?.mimetype || 'image/jpeg';
    return `data:${mime};base64,${cleaned}`;
  }

  openIdImage(file: IFile): void {
    const src = this.getIdImageSrc(file);
    if (!src) return;
    if (src.startsWith('data:')) {
      try {
        const parts = src.split(',');
        const mimeMatch = /data:(.*?);base64/.exec(parts[0]);
        const mime = mimeMatch?.[1] || 'application/octet-stream';
        const binary = atob(parts.slice(1).join(','));
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blobUrl = URL.createObjectURL(new Blob([bytes], { type: mime }));
        const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
        if (win) {
          win.addEventListener('beforeunload', () =>
            URL.revokeObjectURL(blobUrl)
          );
        } else {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        }
      } catch {
        window.open(src, '_blank', 'noopener,noreferrer');
      }
    } else {
      window.open(src, '_blank', 'noopener,noreferrer');
    }
  }

  // ── Read-only drug-card detail (mirrors the insurance tab) ─────────────

  getDrugCardTypeLabel(type: DrugCardType | undefined): string {
    if (!type) return '';
    return DRUG_CARD_TYPE_LABELS[type] ?? type;
  }

  getDrugCardSides(card: IDrugCard): { label: string; fileKey: string }[] {
    const front = (card.frontFileKey ?? '').trim();
    const back = (card.backFileKey ?? '').trim();
    const legacy = (card.fileKey ?? '').trim();
    const out: { label: string; fileKey: string }[] = [];
    if (front) out.push({ label: 'Front', fileKey: front });
    if (back && back !== front) out.push({ label: 'Back', fileKey: back });
    if (out.length === 0 && legacy) out.push({ label: 'Card', fileKey: legacy });
    return out;
  }

  private cardFile(fileKey: string): IFile | undefined {
    // The shared store can hold two entities with the same name but different
    // folders (e.g. a bufferless 'name' twin left by a root loadFiles alongside
    // the real 'drug-cards/name'). Prefer the one that actually has a buffer.
    const matches = this.idFiles.filter(f => f.name === fileKey);
    return (
      matches.find(f => f.buffer) ??
      matches.find(f => f.folder === DRUG_CARDS_FOLDER) ??
      matches[0]
    );
  }

  getCardFile(fileKey: string): IFile | undefined {
    const file = this.cardFile(fileKey);
    return file?.buffer ? file : undefined;
  }

  isCardImageMime(fileKey: string): boolean {
    const mime = this.cardFile(fileKey)?.mimetype;
    return !!mime && mime.startsWith('image/');
  }

  openCardSide(fileKey: string): void {
    const file = this.cardFile(fileKey);
    if (file) this.openIdImage(file);
  }

  // ── ID verification actions ───────────────────────────────────────────

  markIDAsVerified(): void {
    this.isIdImagesModalVisible = false;
    if (this.IDVerificationStatus !== 'verified') {
      this.isExpiryModalVisible = true;
    }
  }

  markUnverifyID(): void {
    this.isIdImagesModalVisible = false;
    this.isVerificationConfirmationModalVisible = true;
  }

  handleVerificationConfirmationCancel(): void {
    this.isVerificationConfirmationModalVisible = false;
  }

  handleVerificationConfirmationOk(): void {
    const newVerifications = {
      ...this.customer.verifications,
      govId: {
        verified: 'required' as VerifiedStatus,
        verifiedBy: this.user.firstName + ' ' + this.user.lastName,
        expiry: null
      }
    };
    this.customerFacade.updateCustomer(this.customer._id, {
      verifications: newVerifications
    });
    this.isVerificationConfirmationModalVisible = false;
    this.verificationChanged.emit();
  }

  handleExpiryDateOk(): void {
    if (this.expiryDate) {
      const newVerifications = {
        ...this.customer.verifications,
        govId: {
          verified: 'verified' as VerifiedStatus,
          verifiedBy: this.user.firstName + ' ' + this.user.lastName,
          expiry: new Date(this.expiryDate).getTime()
        }
      };
      this.customerFacade.updateCustomer(this.customer._id, {
        verifications: newVerifications
      });
      this.verificationChanged.emit();
    }
    this.isExpiryModalVisible = false;
    this.expiryDate = null;
  }

  handleExpiryDateCancel(): void {
    this.isExpiryModalVisible = false;
    this.expiryDate = null;
  }
}
